<script setup lang="ts">
import { DEFAULT_COMMUNITY_PAGE_SIZE } from '~/constants/community'
import { YSC_API_REVIEW_ERROR } from '~/utils/error/YscErrorClasses'

definePageMeta({
  middleware: ['force-page-one-if-empty'],
})

const { localeProperties: { yscCode } } = useLocaleProperties()
const route = useRoute()
const { $api } = useYscApi()
const { t } = useI18n()
useSeoText({ title: t('pages.community_all_reviews.meta.title'), description: t('pages.community_all_reviews.meta.description') })

const page = ref(queryValueToNumber(route.query.page) || 1)
const { status, data: reviews } = await useLazyAsyncData(`page-community-all-reviews-${page.value}`, async () => {
  scrollToTop()

  let reviews
  try {
    reviews = await $api.reviews.getAll({
      language: yscCode,
      has_text: true,
      page_size: DEFAULT_COMMUNITY_PAGE_SIZE,
      page: page.value,
    })
  }
  catch (e) {
    if (e instanceof Error) {
      const { $errorManager } = useErrorManager()
      $errorManager({ e, name: YSC_API_REVIEW_ERROR })
    }
    throw showError({ statusCode: 404 })
  }

  return reviews
}, {
  watch: [route],
})

const { isPending } = useIsPending(status)
</script>

<template>
  <AppPageSection>
    <AppLayoutTwoOneThird class="mb-12">
      <template #two-third>
        <AppLoading v-if="isPending" />

        <div
          class="space-y-12"
        >
          <AppReview
            v-for="review in reviews?.results"
            :key="review.id"
            :id-review="review.id"
            :note="review.avg"
            :photos="review.photos"
          >
            <template #avatar>
              <YscAvatar
                :first-name="review.author.first_name"
                :picture="review.author.photo || undefined"
                radius="28"
              />
            </template>

            <template #user-name>
              {{ review.author.first_name }}
            </template>

            <template
              v-if="review.published_date"
              #user-description
            >
              {{ $d(new Date(review.published_date), 'Y.numeric M.long D.numeric') }}
            </template>

            <template #description>
              <!-- eslint-disable-next-line vue/no-v-html -->
              <p v-html="review.text" />
            </template>
          </AppReview>
        </div>

        <div
          v-if="reviews?.count && reviews.count > DEFAULT_COMMUNITY_PAGE_SIZE"
          class="mt-12"
        >
          <YscPagination
            :page-size="DEFAULT_COMMUNITY_PAGE_SIZE"
            :count="reviews?.count"
          />
        </div>
      </template>
    </AppLayoutTwoOneThird>
  </AppPageSection>
</template>
